import { reactive, computed, ref } from "vue";

/**
 * Vue reactive device resolution detector
 * @param {boolean} watch If true, event listener will be added.
 */
export default (watch = false) => {
  const device = reactive({
    mobile: false,
    width: 0,
  });

  const isEventListenerActive = ref<boolean>(false);

  /**
   * Returns true when window width is minimum 992px or provided value.
   * Similar to css media query.
   * @param {number} value The number of width to check.
   */
  function minWidth(value = 992): boolean {
    return window.matchMedia("only screen and (min-width: " + value + "px)").matches;
  }

  /**
   * Returns true when window width is maximum 992px or provided value.
   * Similar to css media query.
   * @param {number} value The number of width to check.
   */
  function maxWidth(value = 992): boolean {
    return window.matchMedia("only screen and (max-width: " + value + "px)").matches;
  }

  /**
   * Returns true when navigator.userAgent is mobile.
   */
  function isUserAgentMobile(): boolean {
    // /android|webos|avantgo|iphone|ipod|ipad|bolt|boost|cricket|docomo|fone|hiptop|opera mini|mini|kitkat|mobi|palm|phone|pie|tablet|up/i.test(navigator.userAgent);
    return (
      navigator.userAgent.indexOf("Mobi") > -1 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    );
  }

  /**
   * Updates device object with default values.
   */
  const updateDeviceInfo = (): void => {
    device.mobile = maxWidth();
    device.width = document.documentElement.clientWidth;
  };

  /**
   * Adding event listener to window resize event.
   * And updates device object with default values.
   */
  const startWatchingPageSizeChange = () => {
    if (!isEventListenerActive.value) {
      window.addEventListener("resize", updateDeviceInfo);
      isEventListenerActive.value = true;
    }
    updateDeviceInfo();
  };

  /**
   * Removing event listener from window resize event.
   */
  const stopWatchingPageSizeChange = () => {
    window.removeEventListener("resize", updateDeviceInfo);
    isEventListenerActive.value = false;
  };

  /**
   * Initialing device object with default values.
   */
  const init = () => {
    if (watch) {
      startWatchingPageSizeChange();
    } else {
      updateDeviceInfo();
    }
  };

  init();

  return {
    isMobile: computed(() => device.mobile),
    windowWidth: computed(() => device.width),
    minWidth,
    maxWidth,
    isUserAgentMobile,
    updateDeviceInfo,
    startWatchingPageSizeChange,
    stopWatchingPageSizeChange,
  };
};
